import { styled } from "@mui/material/styles";
import {
  Box as MuiBox,
  Drawer as MuiDrawer,
  Toolbar as MuiToolbar,
  IconButton as MuiIconButton,
  Typography as MuiTypography,
  Button as MuiButton,
  TextField as MuiTextField,
  LinearProgress,
} from "@mui/material";
import {
  MenuOpen as MuiMenuOpenIcon,
  Save as MuiSaveIcon,
  RocketLaunch as MuiRocketLaunchIcon,
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import ContentRevisionCard from "./ContentRevisionCard";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { formatModifiedDate } from "app/shared/utils";
import { Formik } from "formik";
import { GET_STUDIES_URL_BASE } from "app/shared/constants";
import axios from "axios";

const drawerWidth = 280;
const revisionFeatureFlagEnabled = false; // set to true to enable version history features (publish button, restore button, version history drawer)

const StudyWebsiteContent = ({ study }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [publishDisabled, setPublishDisabled] = useState(true);
  const [publishAndSave, setPublishAndSave] = useState(true);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [submitAction, setSubmitAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [studyData, setStudyData] = useState(study);

  const getInitialValues = (revision) => ({
    shortTitle: revision?.studyWebsiteContentDto?.shortTitle || "",
    title: revision?.studyWebsiteContentDto?.title || "",
    briefSummary: revision?.studyWebsiteContentDto?.briefSummary || "",
    inclusionCriteria:
      revision?.studyWebsiteContentDto?.inclusionCriteria || "",
    exclusionCriteria:
      revision?.studyWebsiteContentDto?.exclusionCriteria || "",
  });

  // When study data is updated (either from props or after saving), ensure
  // the selected revision is updated to reflect any changes (like new revision
  // created after save)
  useEffect(() => {
    if (!studyData?.studyWebsiteContentRevisions?.length) return;

    // Find the latest draft revision to select by default
    const draft = studyData.studyWebsiteContentRevisions.reduce(
      (max, r) => (max == null || r.id > max.id ? r : max),
      null,
    );

    if (!selectedRevision || selectedRevision.id !== draft.id) {
      setSelectedRevision(draft);
    }
  }, [studyData, selectedRevision]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const saveStudyWebsiteContent = async (values, payload) => {
    if (!studyData.id) return;

    setLoading(true);
    setPublishDisabled(true);
    setPublishAndSave(true);
    try {
      const response = await axios.patch(
        `${GET_STUDIES_URL_BASE}/study-website-content/${studyData.id}`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/merge-patch+json",
          },
        },
      );
      setStudyData(response.data);
      const latestDraft = response.data.studyWebsiteContentRevisions?.find(
        (r) => r.contentStatus === "DRAFT",
      );

      if (latestDraft) {
        setSelectedRevision(latestDraft);
      }
    } catch (err) {
      console.error("Failed to save studyData website content", err);
    }
    setLoading(false);
    setPublishDisabled(false);
    setPublishAndSave(true);
  };

  const handleSubmit = (values) => {
    console.log("Submit values", values);
    const basePayload = {
      studyWebsiteContentRevisions: [
        {
          id: selectedRevision?.id,
          contentStatus: "",
          studyWebsiteContentDto: {
            shortTitle: values.shortTitle,
            title: values.title,
            briefSummary: values.briefSummary,
            inclusionCriteria: values.inclusionCriteria,
            exclusionCriteria: values.exclusionCriteria,
          },
        },
      ],
    };
    // save draft OR publish
    if (submitAction === "save") {
      console.log("Save draft", values);
      const payload = {
        ...basePayload,
        studyWebsiteContentRevisions: [
          {
            ...basePayload.studyWebsiteContentRevisions[0],
            contentStatus: "DRAFT",
          },
        ],
      };
      saveStudyWebsiteContent(values, payload);
    }

    if (submitAction === "publishAndSave") {
      console.log("Publish & Save", values);
      const payload = {
        ...basePayload,
        studyWebsiteContentRevisions: [
          {
            ...basePayload.studyWebsiteContentRevisions[0],
            contentStatus: "PUBLISHED",
          },
        ],
      };
      saveStudyWebsiteContent(values, payload);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(selectedRevision)}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, submitForm, dirty, isValid }) => (
        <MuiBox sx={{ position: "relative", overflow: "hidden" }}>
          <MuiTypography variant="h1" sx={{ mb: 0.5 }}>
            {t("StudyWebisteContentForm.ContentTitle")}
          </MuiTypography>
          <ToolBar>
            <MuiButton
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
              disabled={!dirty || !isValid || loading}
              startIcon={<MuiSaveIcon />}
              onClick={() => {
                setSubmitAction("publishAndSave");
                submitForm();
              }}
            >
              {t("formLabel.buttonSave")}
            </MuiButton>
            {revisionFeatureFlagEnabled && publishAndSave && (
              <MuiButton
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
                disabled={!dirty || !isValid || loading}
                startIcon={<MuiRocketLaunchIcon />}
                onClick={() => {
                  setSubmitAction("publishAndSave");
                  submitForm();
                }}
              >
                {t("StudyWebisteContentForm.publishAndSaveButtonLabel")}
              </MuiButton>
            )}
            {revisionFeatureFlagEnabled && !publishAndSave && (
              <MuiButton
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
                disabled={publishDisabled}
                startIcon={<MuiRocketLaunchIcon />}
                onClick={() => setSubmitAction("publish")}
              >
                {t("StudyWebisteContentForm.publishButtonLabel")}
              </MuiButton>
            )}
            {revisionFeatureFlagEnabled && (
              <Tooltip
                title={
                  open ? "Close Version History" : "Manage Version History"
                }
              >
                <DrawerTrigger
                  open={open}
                  color="inherit"
                  aria-label="open menu drawer"
                  edge="end"
                  onClick={toggleDrawer}
                >
                  <MuiMenuOpenIcon />
                </DrawerTrigger>
              </Tooltip>
            )}
          </ToolBar>
          <Main>
            {loading && <ProgressBar color="secondary" />}

            <MuiTextField
              name="shortTitle"
              multiline
              minRows={1}
              fullWidth
              label={t("StudyWebisteContentForm.studyTitle")}
              variant="outlined"
              onChange={handleChange}
              size="small"
              value={values.shortTitle}
            />

            <MuiTextField
              sx={{ marginTop: "30px" }}
              multiline
              minRows={1}
              name="title"
              fullWidth
              label={t("StudyWebisteContentForm.officialStudyTitle")}
              variant="outlined"
              onChange={handleChange}
              size="small"
              value={values.title}
              disabled={Boolean(studyData?.nctId)}
            />

            <MuiTextField
              sx={{ marginTop: "30px" }}
              multiline
              minRows={1}
              name="briefSummary"
              fullWidth
              label={t("StudyWebisteContentForm.briefStudyOverview")}
              variant="outlined"
              onChange={handleChange}
              size="small"
              value={values.briefSummary}
            />

            <MuiTextField
              sx={{ marginTop: "30px" }}
              multiline
              minRows={4}
              name="inclusionCriteria"
              fullWidth
              label={t("StudyWebisteContentForm.inclusionCriteria")}
              variant="outlined"
              onChange={handleChange}
              size="small"
              value={values.inclusionCriteria}
            />

            <MuiTextField
              sx={{ marginTop: "30px" }}
              multiline
              minRows={4}
              name="exclusionCriteria"
              fullWidth
              label={t("StudyWebisteContentForm.exclusionCriteria")}
              variant="outlined"
              onChange={handleChange}
              size="small"
              value={values.exclusionCriteria}
            />
          </Main>
          <MuiDrawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
              },
            }}
            variant="persistent"
            anchor="right"
            open={open}
            slotProps={{
              paper: {
                sx: {
                  position: "absolute",
                  top: "107px",
                  borderRight: "1px solid #ccc",
                  backgroundColor: "#f4f4f4",
                  padding: "18px",
                },
              },
              backdrop: { style: { position: "absolute" } },
            }}
          >
            <MuiTypography variant="h3" sx={{ fontSize: "18px", mb: 2 }}>
              {t("StudyWebisteContentForm.versionHistory")}
            </MuiTypography>

            {studyData?.studyWebsiteContentRevisions?.map((revision) => (
              <ContentRevisionCard
                key={revision.id}
                title={revision.contentStatus + " " + revision.id}
                createdDate={formatModifiedDate(revision.createdDate)}
                isPublished={revision.contentStatus === "ORIGINAL"}
                isCurrentDraft={revision.id === selectedRevision?.id}
                onClick={() => setSelectedRevision(revision)}
                onClickRestore={
                  revision.contentStatus !== "DRAFT"
                    ? () => {
                        // restore logic here
                        console.log("Restore revision", revision.id);
                      }
                    : undefined
                }
              />
            ))}
          </MuiDrawer>
        </MuiBox>
      )}
    </Formik>
  );
};

StudyWebsiteContent.propTypes = {
  study: PropTypes.shape({
    id: PropTypes.number.isRequired,
    studyWebsiteContentRevisions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        contentStatus: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        studyWebsiteContentDto: PropTypes.shape({
          shortTitle: PropTypes.string,
          title: PropTypes.string,
          briefSummary: PropTypes.string,
          inclusionCriteria: PropTypes.string,
          exclusionCriteria: PropTypes.string,
        }),
      }),
    ),
  }).isRequired,
};

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  border: "1px solid #ccc",
  borderRadius: "0 0 20px 20px",
  position: "relative",
  minHeight: "800px",
  paddingTop: "30px",
}));

const ToolBar = styled(MuiToolbar)(({ theme }) => ({
  display: "flex",
  border: "1px solid #ccc",
  borderRadius: "20px 20px 0 0",
  borderBottom: "none",
}));

const DrawerTrigger = styled(MuiIconButton, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  marginLeft: "auto",
  //border: (theme) => `1px solid ${theme.palette.secondary.outlinedBorder}`,
  border: "1px solid #ccc",
  backgroundColor: "#f4f4f4",
  padding: "3px 8px",
  borderRadius: "4px",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transform: "scaleX(-1)",
      },
    },
  ],
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
}));

export default StudyWebsiteContent;
